import { useCurrentUser } from "../../hooks/useCurrentUser";
import { useState, useEffect } from "react";
import {
  TextField,
  MenuItem,
  Select,
  FormControl,
  Autocomplete,
  Checkbox,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { IoIosArrowBack } from "react-icons/io";
import { fetchAllEntities } from "../../apis/apiService";
import { toast } from "react-toastify";
import { BtnLoader } from "../../components/common/Loader";
import {
  useFetchGetAllProjectBasedUserNameListQuery,
  useFetchGetallUserListQuery,
  useFetchUserAccessedProjectsQuery,
  useGetAllUserWithRoleQuery,
} from "../../slice/logdetailSlice";
import dayjs from "dayjs";
import { fetch2factorUsers } from "../../apis/apiService";



// Filter Project Component
// =============================

export const FilterAppraisalFeedback = ({ onApply, onCancel }) => {
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const {
    id: login_user_id,
    name: login_user_name,
    role: login_user_role,
  } = useCurrentUser();
  const [feedbackList, setFeedbackList] = useState([]);
  const [isFiltered, setIsFiltered] = useState(false);

  // Check if user is employee (case-insensitive)
  const isEmployee = login_user_role?.toLowerCase() === 'employee';

  // State for departments
  const [selectedDepartments, setSelectedDepartments] = useState([]);

  // State for user-based projects
  const [userBasedProjects, setUserBasedProjects] = useState([]);
  const [loadingUserProjects, setLoadingUserProjects] = useState(false);

  // Get all users for the initial dropdown
  const { data: allUsers } = useFetchGetallUserListQuery();
  const { data: allUserWithRoleBased, refetch } = useGetAllUserWithRoleQuery();
  // console.log("getAllUserWithRole :", allUserWithRoleBased);



  // Project-based users (when project is selected)
  const { data: project_based_users } =
    useFetchGetAllProjectBasedUserNameListQuery(selectedProjectId, {
      skip: !selectedProjectId,
    });

  // All projects (existing)
  const {
    data: user_accessed_project,
    isLoading: project_loading,
  } = useFetchUserAccessedProjectsQuery();

  // Fetch projects based on selected user
  const fetchUserAccessedProjects = async (userId) => {
    if (!userId) {
      setUserBasedProjects([]);
      return;
    }

    try {
      setLoadingUserProjects(true);
      const token = localStorage.getItem("access_token");
      const url = `getUserAccessedProject/${userId}`;
      const response = await fetch2factorUsers(url, token);
      setUserBasedProjects(response.data || []);
    } catch (error) {
      console.error("Error fetching user projects:", error);
      setUserBasedProjects([]);
    } finally {
      setLoadingUserProjects(false);
    }
  };

  // State for departments
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [departmentData, setDepartmentData] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);

  // Fetch departments from API
  const fetchDepartments = async () => {
    try {
      setLoadingDepartments(true);
      const response = await fetchAllEntities("master/department/show");

      if (response.status && response.data) {
        setDepartmentData(response.data);
        const deptNames = response.data.map(dept => dept.department_name);

        // Ensure FRONTEND is always included
        const allDepartments = [...new Set([...deptNames])];
        setDepartmentOptions(allDepartments);
      }
    } catch (error) {
      console.error("Error fetching departments:", error);
      // Fallback to hardcoded options if API fails
      setDepartmentOptions(["ADMINISTRATOR", "BACKEND", "DESIGNER", "TESTER"]);
    } finally {
      setLoadingDepartments(false);
    }
  };

  // Fetch departments on component mount
  useEffect(() => {
    fetchDepartments();
  }, []);

  // 👇 ADD THIS NEW useEffect HERE (around line 80-90)
  // Load saved filter values from sessionStorage when component mounts
  useEffect(() => {
    const savedFilter = sessionStorage.getItem("appraisalFilter");
    if (savedFilter) {
      try {
        const parsedFilter = JSON.parse(savedFilter);

        // Set project_id
        if (parsedFilter.project_ids && parsedFilter.project_ids.length > 0) {
          const projectId = parsedFilter.project_ids[0];
          setValue("project_id", projectId);
          setSelectedProjectId(projectId);
        }

        // Set owner/user
        if (parsedFilter.user_ids && parsedFilter.user_ids.length > 0) {
          const userId = parsedFilter.user_ids[0];
          setValue("owner", userId);
          setSelectedUserId(userId);
          fetchUserAccessedProjects(userId);
        }

        // Set date
        if (parsedFilter.date_of_incident) {
          setValue("createdDate", dayjs(parsedFilter.date_of_incident));
        }

        // Set feedback type
        if (parsedFilter.feedback_type) {
          setValue("feedBackType", parsedFilter.feedback_type);
        }

        // Set departments
        if (parsedFilter.departments && parsedFilter.departments.length > 0) {
          setValue("departments", parsedFilter.departments);
          setSelectedDepartments(parsedFilter.departments);
        }

      } catch (error) {
        console.error("Error parsing saved filter:", error);
      }
    }
  }, []); // Empty dependency array means this runs once on component mount


  const { handleSubmit, control, reset, setValue, watch, trigger } = useForm({
    defaultValues: {
      project_id: null,
      owner: "",
      createdDate: null,
      feedBackType: "",
      departments: [],
    },
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Watch fields
  const ownerValue = watch("owner");
  const projectIdValue = watch("project_id");

  // Effect to fetch projects when user changes
  useEffect(() => {
    if (ownerValue) {
      // console.log("User selected, fetching projects for user:", ownerValue);
      setSelectedUserId(ownerValue);
      fetchUserAccessedProjects(ownerValue);
    } else {
      setSelectedUserId(null);
      setUserBasedProjects([]);
    }
  }, [ownerValue, setValue]);

  // Effect to handle project selection
  useEffect(() => {
    if (projectIdValue) {
      // console.log("Project selected:", projectIdValue);
      setSelectedProjectId(projectIdValue);
    } else {
      setSelectedProjectId(null);
    }
  }, [projectIdValue, setValue]);


  // check getallluserlist with role based

  useEffect(() => {
    refetch()
  }, [selectedProjectId])

  // Get final project options based on selection
  const getFinalProjectOptions = () => {
    if (selectedUserId && userBasedProjects.length > 0) {
      return userBasedProjects;
    }
    return user_accessed_project || [];
  };

  // Get available users for dropdown based on selection
  const getAvailableUsers = () => {
    if (selectedProjectId && project_based_users && project_based_users.length > 0) {
      return project_based_users;
    }

    const usersList = Array.isArray(allUsers) ? [...allUsers] : [];

    const currentUserExists = usersList.some(u => u.user_id === Number(login_user_id));
    if (!currentUserExists && login_user_id) {
      return [
        {
          user_id: Number(login_user_id),
          name: `${login_user_name} (You)`
        },
        ...usersList
      ];
    }

    return usersList;
  };


  //   const onSubmit = async (data) => {
  //     setIsSubmitting(true);
  //     try {
  //       const requestedData = {};

  //       // Send as arrays (even for single values) as per API docs
  //       if (data.project_id) requestedData.project_ids = [data.project_id];
  //       if (data.owner) requestedData.user_ids = [data.owner];
  //       if (data.feedBackType) requestedData.feedback_type = data.feedBackType;
  //       // if (data.createdDate) {
  //       //   requestedData.date_of_incident = format(
  //       //     new Date(data.createdDate),
  //       //     "yyyy-MM-dd"
  //       //   );
  //       // }

  //       if (data.createdDate) {
  //      const selectedDate = dayjs(data.createdDate);

  //       if (!selectedDate.isValid()) {
  //       toast.error("Invalid date value");
  //       setIsSubmitting(false);
  //       return;
  //     }

  //     requestedData.date_of_incident = selectedDate.format("YYYY-MM-DD");
  //     }


  //       // Convert department names to IDs for API
  //       if (data.departments && data.departments.length > 0) {
  //         const departmentIds = data.departments.map(deptName => {
  //           const dept = departmentData.find(d => d.department_name === deptName);
  //           return dept ? dept.department_id : null;
  //         }).filter(Boolean);

  //         if (departmentIds.length > 0) {
  //           requestedData.department_ids = departmentIds;
  //         }

  //         // Also store department names for client-side filtering
  //         requestedData.departments = data.departments;
  //       }

  //       console.log("Filter request data:", requestedData);

  //       const response = await fetchAllEntities(
  //         "appraisalFeedback/getAppraisalFeedbacks",
  //         requestedData
  //       );

  //       if (response?.status) {
  //         sessionStorage.setItem("appraisalFilter", JSON.stringify(requestedData));
  //         let feedbackData = response.data?.data || response.data || [];

  //         // Client-side filtering for departments if API doesn't handle it
  //         // let filteredData = feedbackData;
  //         // if (data.departments && data.departments.length > 0) {
  //         //   filteredData = feedbackData.filter(item => 
  //         //     data.departments.includes(item.department_name)
  //         //   );
  //         // }

  //         let filteredData = feedbackData.filter(item => {
  //   let match = true;

  //   if (data.project_id) {
  //     match = match && Number(item.project_id) === Number(data.project_id);
  //   }

  //   if (data.owner) {
  //     match = match && Number(item.user_id) === Number(data.owner);
  //   }

  //   if (data.feedBackType) {
  //     match =
  //       match &&
  //       item.feedback_type?.toLowerCase() === data.feedBackType.toLowerCase();
  //     }

  //   if (data.createdDate) {
  //     const selectedDate = dayjs(data.createdDate).format("YYYY-MM-DD");
  //     match =
  //       match &&
  //       dayjs(item.date_of_incident).format("YYYY-MM-DD") ===
  //         selectedDate;
  //   }

  //   if (data.departments && data.departments.length > 0) {
  //     match =
  //       match &&
  //       data.departments.includes(item.department_name);
  //   }
  //   return match;
  // });

  //         onApply(filteredData, requestedData);
  //         onCancel();
  //       } else {
  //         toast.error("No data found with the selected filters");
  //         onApply([], requestedData);
  //       }
  //     } catch (error) {
  //       toast.error(error.message || "Filter failed");
  //     } finally {
  //       setIsSubmitting(false);
  //     }
  //   };

  // const handleClear = () => {
  //    sessionStorage.removeItem("appraisalFilter");
  //   reset({
  //     project_id: null,
  //     owner: "",
  //     createdDate: null,
  //     feedBackType: "",
  //     departments: [],
  //   });
  //   setSelectedProjectId(null);
  //   setSelectedUserId(null);
  //   setUserBasedProjects([]);
  //   setSelectedDepartments([]);
  // };

  // Clear button for individual fields

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const requestedData = {};

      // Send as arrays (even for single values) as per API docs
      if (data.project_id) requestedData.project_ids = [data.project_id];
      if (data.owner) requestedData.user_ids = [data.owner];
      if (data.feedBackType) requestedData.feedback_type = data.feedBackType;

      // Validate date before proceeding
      if (data.createdDate) {
        const selectedDate = dayjs(data.createdDate);

        // Check if date is valid
        if (!selectedDate.isValid()) {
          toast.error("Invalid date format");
          setIsSubmitting(false);
          return;
        }

        // Check if date is before 2000
        if (selectedDate.isBefore(dayjs("2000-01-01"), "day")) {
          toast.error("Date cannot be before 2000");
          setIsSubmitting(false);
          return;
        }

        // Check if date is in the future
        if (selectedDate.isAfter(dayjs(), "day")) {
          toast.error("Date cannot be in the future");
          setIsSubmitting(false);
          return;
        }

        requestedData.date_of_incident = selectedDate.format("YYYY-MM-DD");
      }
      // 🔥 ADD HERE
      if (data.departments && data.departments.length > 0) {
        const departmentIds = data.departments
          .map((deptName) => {
            const dept = departmentData.find(
              (d) => d.department_name === deptName
            );
            return dept ? dept.department_id : null;
          })
          .filter(Boolean);

        if (departmentIds.length > 0) {
          requestedData.department_ids = departmentIds;
        }

        requestedData.departments = data.departments;
      }


      else {
        // If no date selected, proceed without date filter
        // console.log("No date selected, proceeding without date filter");
      }

      // Rest of your code...
      // console.log("Filter request data:", requestedData);

      const response = await fetchAllEntities(
        "appraisalFeedback/getAppraisalFeedbacks",
        requestedData
      );

      if (response?.status) {
        sessionStorage.setItem("appraisalFilter", JSON.stringify(requestedData));
        let feedbackData = response.data?.data || response.data || [];

        // Only apply client-side filtering if we have data from API
        let filteredData = feedbackData;

        // If date was provided, do client-side filtering for date
        if (data.createdDate) {
          const selectedDate = dayjs(data.createdDate).format("YYYY-MM-DD");
          filteredData = feedbackData.filter(item =>
            dayjs(item.date_of_incident).format("YYYY-MM-DD") === selectedDate
          );
        }

        // Apply other filters
        filteredData = filteredData.filter(item => {
          let match = true;

          if (data.project_id) {
            match = match && Number(item.project_id) === Number(data.project_id);
          }

          if (data.owner) {
            match = match && Number(item.user_id) === Number(data.owner);
          }

          if (data.feedBackType) {
            match = match && item.feedback_type?.toLowerCase() === data.feedBackType.toLowerCase();
          }

          if (data.departments && data.departments.length > 0) {
            match = match && data.departments.includes(item.department_name);
          }
          return match;
        });

        onApply(filteredData, requestedData);
        onCancel();
      } else {
        toast.error("No data found with the selected filters");
        onApply([], requestedData);
      }
    } catch (error) {
      console.error("Filter error:", error);
      toast.error(error.message || "Filter failed");
    } finally {
      setIsSubmitting(false);
    }
  };


  const handleClear = async () => {
    sessionStorage.removeItem("appraisalFilter");

    reset({
      project_id: null,
      owner: "",
      createdDate: null,
      feedBackType: "",
      departments: [],
    });

    setSelectedProjectId(null);
    setSelectedUserId(null);
    setUserBasedProjects([]);
    setSelectedDepartments([]);

    try {
      const response = await fetchAllEntities(
        "appraisalFeedback/getAppraisalFeedbacks"
      );

      if (response?.status) {
        const allData = response.data?.data || response.data || [];

        // Pass null as the second parameter to indicate no filters are applied
        onApply(allData, null);
      }
    } catch (error) {
      console.error("Error fetching all records:", error);
    }
  };


  const handleClearProject = () => {
    setValue("project_id", null);
    setSelectedProjectId(null);
  };

  const handleClearUser = () => {
    setValue("owner", "");
    setSelectedUserId(null);
    setUserBasedProjects([]);
  };

  // Add this hook in your component
  const useTheme = () => {
    const [isDarkMode, setIsDarkMode] = useState(false);

    useEffect(() => {
      // Check if dark mode is enabled (adjust based on your theme implementation)
      const checkTheme = () => {
        const dark = document.body.classList.contains('dark-mode') ||
          document.documentElement.getAttribute('data-theme') === 'dark';
        setIsDarkMode(dark);
      };

      checkTheme();

      // Optional: Observe theme changes if they can change dynamically
      const observer = new MutationObserver(checkTheme);
      observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

      return () => observer.disconnect();
    }, []);

    return isDarkMode;
  };

  const isDarkMode = useTheme();
  const selectedTextColor = isDarkMode ? '#ffffff' : '#333333'; // White in dark, dark gray in light

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div className="filter-h-bg px-4 py-3 d-flex justify-content-between">
        <button type="button" onClick={onCancel} className="R_btn">
          <div className="d-flex text-rgba-gray align-items-center form-label">
            <IoIosArrowBack className="me-1" /> Filter
          </div>
        </button>
        <button type="button" onClick={handleClear} className="R_btn tex-tgba-red clear-filter-btn">
          Clear Filter
        </button>
      </div>

      {/* Make this div scrollable with max height */}
      <div className="bg-white px-4 py-3" style={{
        flex: 1,
        overflowY: 'auto',
        maxHeight: 'calc(100vh - 140px)' // Adjust based on header and button heights
      }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="">
              {/* PROJECT */}
              <div className="row mb-3">
                <div className="col-md-12">
                  <div className="d-flex justify-content-between align-items-center">
                    <label htmlFor="project_id" className="from-label mb-2">
                      Project
                    </label>
                  </div>
                  <Controller
                    name="project_id"
                    control={control}
                    render={({ field }) => {
                      const projectOptions = getFinalProjectOptions();
                      const currentProject = projectOptions.find(
                        p => p.project_id === field.value
                      );

                      return (
                        <Autocomplete
                          options={projectOptions}
                          getOptionLabel={(o) => o?.title || ""}
                          value={currentProject || null}
                          loading={project_loading || loadingUserProjects}
                          onChange={(e, v) => {
                            const projectId = v?.project_id || null;
                            field.onChange(projectId);
                            setSelectedProjectId(projectId);
                          }}
                          filterOptions={(options, { inputValue }) => {
                            // INGA MATTUM PODU:
                            if (selectedUserId && userBasedProjects.length > 0) {
                              return options.filter(option =>
                                option.title?.toLowerCase().includes(inputValue?.toLowerCase() || '')
                              );
                            }
                            return options.filter(option =>
                              option.title?.toLowerCase().includes(inputValue?.toLowerCase() || '')
                            );
                          }}
                          renderOption={(props, option, { selected }) => (
                            <li
                              {...props}
                              className="d-flex align-items-center mb-2"
                            >
                              <Checkbox
                                style={{ marginRight: 8 }}
                                checked={selected}
                              />
                              <strong>{typeof option === "string" ? option : option?.title || ""}</strong>
                            </li>
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder={
                                selectedUserId && userBasedProjects.length === 0 && !loadingUserProjects
                                  ? "No projects found for this user"
                                  : "Select project"
                              }
                              sx={{
                                "& .MuiInputBase-input::placeholder": { color: "#999999" },
                                "& .MuiInputBase-input": { color: field.value ? "#000000" : "#999999" },
                              }}
                            />
                          )}
                        />
                      );
                    }}
                  />
                  {selectedUserId && userBasedProjects.length === 0 && !loadingUserProjects && (
                    <div className="text-muted small mt-1">
                      No projects found for this user
                    </div>
                  )}
                </div>
              </div>

              {/* USER - Only show for non-employee users (manager, admin, etc.) */}
              {!isEmployee && (
                <div className="col-12 mt-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <label htmlFor="owner" className="from-label mb-2">
                      Users
                    </label>
                  </div>
                  <Controller
                    name="owner"
                    control={control}
                    render={({ field }) => {
                      const availableUsers = getAvailableUsers() || [];
                      const currentUser = availableUsers.find(u => u.user_id === Number(field.value)) || null;

                      return (
                        <Autocomplete
                          options={availableUsers}
                          getOptionLabel={(option) => option?.name || ""}
                          value={currentUser}
                          onChange={(e, value) => {
                            const userId = value?.user_id || "";
                            field.onChange(userId);
                            setSelectedUserId(userId);
                            fetchUserAccessedProjects(userId);
                          }}
                          filterOptions={(options, { inputValue }) => {
                            return options.filter(option =>
                              option.name?.toLowerCase().includes(inputValue?.toLowerCase() || '')
                            );
                          }}
                          renderOption={(props, option, { selected }) => (
                            <li
                              {...props}
                              className="d-flex align-items-center mb-2"
                            >
                              <Checkbox
                                style={{ marginRight: 8 }}
                                checked={selected}
                              />
                              <strong>{typeof option === "string" ? option : option?.name || ""}</strong>
                            </li>
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder={
                                selectedProjectId && project_based_users?.length === 0
                                  ? "No users found for this project"
                                  : "Select Users"
                              }
                              sx={{
                                "& .MuiInputBase-input::placeholder": { color: "#999999" },
                                "& .MuiInputBase-input": { color: field.value ? "#000000" : "#999999" },
                              }}
                            />
                          )}
                          disabled={selectedProjectId && project_based_users?.length === 0}
                        />
                      );
                    }}
                  />
                  {selectedProjectId && project_based_users && project_based_users.length === 0 && (
                    <div className="text-muted small mt-1">
                      No users found for this project
                    </div>
                  )}
                </div>
              )}

              {/* DEPARTMENT MULTI-SELECT - Only show for non-employee users */}
              {!isEmployee && (
                <div className="col-12 mt-3 department-select-wrapper">
                  <label htmlFor="departments" className="form-label mb-2">
                    Department
                  </label>

                  <Controller
                    name="departments"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth className="mt-2">
                        <Select
                          multiple
                          displayEmpty
                          value={field.value || []}
                          onChange={(e) => {
                            field.onChange(e.target.value);
                            setSelectedDepartments(e.target.value);
                          }}
                          renderValue={(selected) => {
                            if (selected.length === 0) {
                              return (
                                <span className="lightTextDeptPlaceholder">
                                  Select Department
                                </span>
                              );
                            }
                            return (
                              <span className="selected-value-text">
                                {selected.join(", ")}
                              </span>
                            );
                          }}
                          MenuProps={{
                            PaperProps: {
                              style: { maxHeight: 250 },
                            },
                          }}
                        >
                          {departmentOptions.map((dept) => (
                            <MenuItem key={dept} value={dept}>
                              <Checkbox
                                checked={field.value?.includes(dept) || false}
                                style={{ marginRight: 8 }}
                              />
                              {dept}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </div>
              )}

              {/* DATE & FEEDBACK TYPE */}
              <div className="row d-flex flex-row">
                {/* <div className="col-md-6 mt-2">
                  <div className="d-flex flex-column mt-3">
                    <label htmlFor="feedBackType" className="from-label ">
                      Date Of Incident
                    </label>
                    <Controller
                      name="createdDate"
                      control={control}
                      render={({ field }) => (
//  <DatePicker
//   {...field}
//   format="DD/MM/YYYY"
//   maxDate={dayjs()}
//   minDate={dayjs("2000-01-01")}
//   disableFuture
//   className="mt-2"

//   // ✅ Lock background scroll when open
//   onOpen={() => {
//     document.body.style.overflow = "hidden";
//   }}

//   // ✅ Restore scroll when closed
//   onClose={() => {
//     document.body.style.overflow = "unset";
//     setTimeout(() => {
//       trigger("dataOfIncident");
//     }, 0);
//   }}

//   onChange={(date) => {
//     field.onChange(date);
//     setTimeout(() => {
//       trigger("dataOfIncident");
//     }, 0);
//   }}

//   sx={{
//     "& .MuiPickersDay-root.Mui-disabled": {
//       color: (theme) =>
//         theme.palette.mode === "dark" ? "#555555" : undefined,
//       opacity: (theme) =>
//         theme.palette.mode === "dark" ? 0.4 : undefined,
//     },
//     "& .MuiPickersPopper-paper": {
//       maxHeight: "400px",
//       overflowY: "auto",
//     },
//   }}

//   slotProps={{
//     textField: {
//       fullWidth: true,
//       sx: {
//         "& .MuiInputBase-input::placeholder": {
//           color: "#999999",
//         },
//         "& .MuiInputBase-input": {
//           color: field.value ? "#000000" : "#999999",
//         },
//       },
//       onBlur: () => {
//         trigger("dataOfIncident");
//       },
//     },
//     popper: {
//       placement: "bottom-start",
//       modifiers: [
//         {
//           name: "eventListeners",
//           options: {
//             scroll: true,   //  allow popper to move with scroll
//             resize: true,
//           },
//         },
//         {
//           name: "preventOverflow",
//           options: {
//             altAxis: true,
//             tether: false,
//           },
//         },
//         {
//           name: "flip",
//           options: {
//             fallbackPlacements: ["top-start"],
//           },
//         },
//         {
//           name: "hide",
//           enabled: true,
//         },
//       ],
//     },
//   }}
// />

<DatePicker
  {...field}
  format="DD/MM/YYYY"
  maxDate={dayjs()}
  minDate={dayjs("2000-01-01")}
  disableFuture
  className="mt-2"

  // Disable dates before 2000-01-01 in calendar
  shouldDisableDate={(date) => {
    return dayjs(date).isBefore(dayjs("2000-01-01"), "day");
  }}

  // Add custom validation for manual input
  onError={(error) => {
    if (error) {
      // You can handle error state here if needed
      console.log("Date error:", error);
    }
  }}

  // Validate on change
  onChange={(date) => {
    // Check if date is valid and not before 2000
    if (date && dayjs(date).isValid()) {
      if (dayjs(date).isBefore(dayjs("2000-01-01"), "day")) {
        // Return without updating - keeps previous valid date
        return;
      }
      field.onChange(date);
    } else {
      // Handle invalid date - clear it
      field.onChange(null);
    }
    setTimeout(() => {
      trigger("dataOfIncident");
    }, 0);
  }}

  // Custom input validation
  slotProps={{
    textField: {
      fullWidth: true,
      error: false, // Don't show MUI error
      helperText: "", // Remove helper text
      sx: {
        "& .MuiInputBase-input::placeholder": {
          color: "#999999",
        },
        "& .MuiInputBase-input": {
          color: field.value ? "#000000" : "#999999",
        },
      },
      onBlur: (e) => {
        const value = e.target.value;
        if (value) {
          const parsedDate = dayjs(value, "DD/MM/YYYY");
          if (!parsedDate.isValid() || parsedDate.isBefore(dayjs("2000-01-01"), "day")) {
            // Clear invalid date
            field.onChange(null);
          }
        }
        trigger("dataOfIncident");
      },
    },
    popper: {
      placement: "bottom-start",
      modifiers: [
        {
          name: "eventListeners",
          options: {
            scroll: true,
            resize: true,
          },
        },
        {
          name: "preventOverflow",
          options: {
            altAxis: true,
            tether: false,
          },
        },
        {
          name: "flip",
          options: {
            fallbackPlacements: ["top-start"],
          },
        },
        {
          name: "hide",
          enabled: true,
        },
      ],
    },
  }}
/>

                      )}
                    />
                  </div>
                </div> */}

                <div className="col-md-6 mt-2">
                  <div className="d-flex flex-column mt-3">
                    <label htmlFor="createdDate" className="from-label ">
                      Date Of Incident
                    </label>
                    <Controller
                      name="createdDate"
                      control={control}
                      rules={{
                        validate: (value) => {
                          if (!value) return true; // Optional field in filter

                          const input = value?.format
                            ? value.format("DD/MM/YYYY")
                            : value;
                          const parts = input.split("/");

                          if (parts.length !== 3) {
                            return "Please enter a valid date.";
                          }

                          const day = parseInt(parts[0], 10);
                          const month = parseInt(parts[1], 10);
                          const year = parseInt(parts[2], 10);

                          const currentYear = dayjs().year();

                          // Year validation
                          if (!year || parts[2].length !== 4) {
                            return "Please enter a valid year.";
                          }

                          if (year < 2000 || year > currentYear) {
                            return "Please enter a valid year.";
                          }

                          // Month validation
                          if (!month || month < 1 || month > 12) {
                            return "Please enter a valid month.";
                          }

                          // Day validation basic check
                          if (!day || day < 1 || day > 31) {
                            return "Please enter a valid date.";
                          }

                          // Full strict date check
                          const date = dayjs(input, "DD/MM/YYYY", true);
                          if (!date.isValid()) {
                            return "Please enter a valid date.";
                          }

                          // Future date - FIXED: Use date.isAfter, not year.isAfter
                          if (date.isAfter(dayjs(), "day")) {
                            return "Future dates are not allowed.";
                          }

                          return true;
                        }
                      }}
                      render={({ field, fieldState }) => (
                        <DatePicker
                          {...field}
                          value={field.value || null}
                          format="DD/MM/YYYY"
                          maxDate={dayjs()}
                          minDate={dayjs("2000-01-01")}
                          disableFuture
                          className="mt-2"

                          onOpen={() => {
                            const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
                            document.body.style.overflow = 'hidden';
                            document.body.style.paddingRight = `${scrollBarWidth}px`;
                          }}

                          onClose={() => {
                            document.body.style.overflow = '';
                            document.body.style.paddingRight = '';
                            setTimeout(() => {
                              trigger("createdDate");
                            }, 0);
                          }}

                          // Disable dates before 2000-01-01 in calendar
                          shouldDisableDate={(date) => {
                            return dayjs(date).isBefore(dayjs("2000-01-01"), "day");
                          }}

                          // Validate on change
                          onChange={(date) => {
                            field.onChange(date);
                            setTimeout(() => {
                              trigger("createdDate");
                            }, 0);
                          }}

                          sx={{
                            '& .MuiPickersDay-root.Mui-disabled': {
                              color: (theme) =>
                                theme.palette.mode === 'dark' ? '#555555' : undefined,
                              opacity: (theme) =>
                                theme.palette.mode === 'dark' ? 0.4 : undefined,
                            },
                            '& .MuiPickersPopper-paper': {
                              maxHeight: '400px',
                              overflowY: 'auto',
                            },
                          }}

                          slotProps={{
                            textField: {
                              fullWidth: true,
                              error: !!fieldState.error,
                              helperText: fieldState.error?.message || "",
                              sx: {
                                "& .MuiInputBase-input::placeholder": {
                                  color: "#999999",
                                },
                                "& .MuiInputBase-input": {
                                  color: field.value ? "#000000" : "#999999",
                                },
                              },
                              onBlur: () => {
                                trigger("createdDate");
                              },
                            },
                            popper: {
                              placement: "bottom-start",
                              modifiers: [
                                {
                                  name: "eventListeners",
                                  options: {
                                    scroll: true,
                                    resize: true,
                                  },
                                },
                                {
                                  name: "preventOverflow",
                                  options: {
                                    altAxis: true,
                                    tether: false,
                                  },
                                },
                                {
                                  name: "flip",
                                  options: {
                                    fallbackPlacements: ["top-start"],
                                  },
                                },
                                {
                                  name: "hide",
                                  enabled: true,
                                },
                              ],
                            },
                          }}
                        />
                      )}
                    />
                  </div>
                </div>

                <div className="col-md-6 mt-4 department-select-wrapper">
                  <label htmlFor="feedBackType" className="from-label ">
                    Feedback Type
                  </label>
                  <Controller
                    name="feedBackType"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth className="mt-2">
                        <Select
                          className="feedback-select"
                          value={field.value || ""}
                          displayEmpty
                          renderValue={(selected) => {
                            if (!selected) {
                              return <span className="lightTextDeptPlaceholder">Select Feedback</span>;
                            }
                            // Use CSS variable or theme-aware class
                            return <span className="selected-value-text">
                              {selected === "positive" ? "Positive" : "Negative"}
                            </span>;
                          }}
                          onChange={(e) => field.onChange(e.target.value)}
                          sx={{
                            '& .MuiSelect-select': {
                              color: field.value ? 'var(--selected-text-color)' : '#565656',
                            }
                          }}
                        >
                          <MenuItem value="positive">Positive</MenuItem>
                          <MenuItem value="negative">Negative</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  />
                </div>
              </div>
            </div>
          </form>
        </LocalizationProvider>
      </div>

      {/* Buttons fixed at bottom */}
      <div className="bg-white w-100 py-3 px-4">
        <div className="d-flex justify-content-left">
          <button
            type="submit"
            className="add_btn me-3"
            onClick={handleSubmit(onSubmit)}
            disabled={isSubmitting}
          >
            {isSubmitting ? <BtnLoader /> : "Apply"}
          </button>
          <button
            type="button"
            className="cancel_btn"
            onClick={onCancel}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};